import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useDocsI18n } from '../../i18n';
import { 
    FolderOpen, 
    Plus, 
    Pencil, 
    Trash2, 
    Save,
    Shield
} from 'lucide-react';

export function AdminProjects({ 
    projects, 
    docs,
    onSave,
    onEdit,
    onDelete
}) {
    const { t } = useDocsI18n();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ 
        name: '', 
        slug: '', 
        description: '', 
        icon: '', 
        color: '#00F0FF', 
        is_admin_only: false 
    });

    const resetForm = () => {
        setForm({ name: '', slug: '', description: '', icon: '', color: '#00F0FF', is_admin_only: false });
        setEditing(null);
    };

    const editProject = (project) => {
        setForm({
            name: project.name,
            slug: project.slug,
            description: project.description,
            icon: project.icon || '',
            color: project.color,
            is_admin_only: project.is_admin_only || false
        });
        setEditing(project);
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await onSave(form, editing);
        setDialogOpen(false);
        resetForm();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-mono font-bold">Projekte</h1>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={resetForm} data-testid="new-project-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            {t('newProject')}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-mono">
                                {editing ? t('editProject') : t('newProject')}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label>{t('name')}</Label>
                                <Input
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder={t('namePh')}
                                    required
                                    data-testid="project-name-input"
                                />
                            </div>
                            <div>
                                <Label>{t('slug')}</Label>
                                <Input
                                    value={form.slug}
                                    onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    placeholder={t('slugPh')}
                                    required
                                    data-testid="project-slug-input"
                                />
                            </div>
                            <div>
                                <Label>{t('description')}</Label>
                                <Textarea
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    placeholder={t('descPh')}
                                    required
                                    data-testid="project-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>{t('icon')}</Label>
                                    <Input
                                        value={form.icon}
                                        onChange={e => setForm({ ...form, icon: e.target.value })}
                                        placeholder="🚗"
                                    />
                                </div>
                                <div>
                                    <Label>{t('color')}</Label>
                                    <Input
                                        type="color"
                                        value={form.color}
                                        onChange={e => setForm({ ...form, color: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                                <Switch
                                    checked={form.is_admin_only}
                                    onCheckedChange={v => setForm({ ...form, is_admin_only: v })}
                                />
                                <div>
                                    <Label className="mb-0">{t('adminsOnly')}</Label>
                                    <p className="text-xs text-muted-foreground">{t('adminsOnlyHint')}</p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" data-testid="save-project-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    Speichern
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map(project => (
                    <Card key={project.id} className="border-border/50 group">
                        <CardHeader>
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                    {project.icon ? (
                                        <span className="text-2xl">{project.icon}</span>
                                    ) : (
                                        <div className="w-8 h-8 rounded" style={{ backgroundColor: project.color }} />
                                    )}
                                    <div>
                                        <CardTitle className="font-mono text-lg">{project.name}</CardTitle>
                                        <CardDescription className="font-mono text-xs">/{project.slug}</CardDescription>
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button variant="ghost" size="icon" onClick={() => editProject(project)}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onDelete(project)}
                                    >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
                            <div className="flex items-center gap-2 mt-4 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                    {docs.filter(d => d.project_id === project.id).length} Docs
                                </Badge>
                                {project.is_admin_only && (
                                    <Badge variant="secondary" className="text-xs">
                                        <Shield className="w-3 h-3 mr-1" />
                                        Nur Admins
                                    </Badge>
                                )}
                                <Link to={`/docs/${project.slug}`} className="text-xs text-primary hover:underline ml-auto">
                                    Ansehen →
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {projects.length === 0 && (
                    <div className="col-span-full text-center py-12 border border-dashed border-border rounded-xl">
                        <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('noProjectsYet')}</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
